import {
    ApplicationInsights,
    ITelemetryItem,
    SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { sanitizeUrl } from '@uipath/portal-shell-util';

import { getEnvConfigField } from '../EnvironmentUtil';
import {
    getAccountName,
    getSpaceName,
    getTenantName,
} from '../urlHelper';
import Logger from './logger';

export function initializeAppInsights() {
    const appInsightsKey = getEnvConfigField('APPINSIGHTS_KEY')
        || (window as any).env?.APPLICATION_INSIGHTS_INSTRUMENTATION_KEY;
    const appInsightsString = getEnvConfigField('APPINSIGHTS_CONNECTION_STRING')
        || (window as any).env?.APPLICATION_INSIGHTS_CONNECTION_STRING;

    const noKeyOrString = !appInsightsKey && !appInsightsString;
    if (noKeyOrString) {
        Logger.info('appInsights', 'AppInsights will not be initialized because key or connection string is missing');
        return null;
    }

    try {
        const appInsights = new ApplicationInsights(
            {
                config: {
                    instrumentationKey: appInsightsKey,
                    connectionString: appInsightsString,
                    accountId: getAccountName(),
                    appId: getSpaceName(),
                },
            });

        appInsights.loadAppInsights();

        const telemetryInitializer = (envelope: ITelemetryItem) => {
            if (envelope.tags) {
                envelope.tags['ai.cloud.role'] = 'apollo';
                envelope.tags['apollo.spaceName'] = getSpaceName();
                envelope.tags['apollo.accountName'] = getAccountName();
                envelope.tags['apollo.tenantName'] = getTenantName();
            }

            // PLT-13335: Sanitize query / token parameters
            if (envelope.data?.baseData?.url) {
                const originalUrl = envelope.data.baseData.url;
                envelope.data.baseData.url = sanitizeUrl(originalUrl);
            }
        };

        appInsights.addTelemetryInitializer(telemetryInitializer);

        appInsights.trackPageView({
            name: document.title,
            // PLT-13335: Sanitize query / token parameters
            uri: sanitizeUrl(location.href),
        });
        appInsights.trackPageViewPerformance({
            name: document.title,
            // PLT-13335: Sanitize query / token parameters
            uri: sanitizeUrl(location.href),
        });

        addGlobalUnHandledExceptionCatcher(appInsights);

        Logger.debug('appInsights', 'AppInsights initialized');
        return appInsights;
    } catch (exception) {
        Logger.error('appInsights', 'Error initializing AppInsights, exception: ' + JSON.stringify(exception));
        return null;
    }
}

function addGlobalUnHandledExceptionCatcher(appInsights: ApplicationInsights) {
    Logger.info('appInsights', 'initializing globalUnhandledExceptionCatcher');
    window.addEventListener('error', (event: ErrorEvent) => {

        // Get the error properties from the error event object
        const {
            message, filename, lineno, colno, error,
        } = event;

        // If the error is not from an apollo package, we let it bubble
        if (!filename?.includes('/apollo-packages/')) {
            return;
        }

        appInsights.trackTrace({
            message: '[globalUnhandledExceptionCatcher] - ' + message,
            severityLevel: SeverityLevel.Critical,
        }, {
            component: 'globalUnhandledExceptionCatcher',
            object: {
                error,
                filename,
                lineno,
                colno,
            },
            stack: error?.stack,
            accountName: getAccountName(),
            tenantName: getTenantName(),
        });

        event.stopPropagation();
    });
}

